import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { functionalUpdate } from "@tanstack/react-table";
import { RootState } from "@/components/state/store";
import { setTableColumnPinning } from "@/components/state/tableSearch/tableConfigSlice";
import {
  getColumnPinningFromColumns,
  normalizePinning,
} from "@/components/tables/genericCrudTable.utils";

interface UseColumnPinningParams {
  columns: any[];
  entity: string;
  enableRowSelection?: boolean | ((row: any) => boolean);
  showRowActions?: boolean;
}

export function useColumnPinning(params: UseColumnPinningParams) {
  const { columns, entity, enableRowSelection, showRowActions } = params;

  const dispatch = useDispatch();
  const globalTableConfig: any =
    useSelector((state: RootState | any) => state.tableConfig) ?? {};

  const [columnPinning, setColumnPinning] = useState<any>({
    left: [],
    right: [],
  });

  const columnPinningFromColumns = getColumnPinningFromColumns(columns);

  useEffect(() => {
    const entityData = globalTableConfig[entity];
    const pinningForEntity = entityData?.columnPinning;

    if (pinningForEntity) {
      try {
        const parsedPinning =
          typeof pinningForEntity === "string"
            ? JSON.parse(pinningForEntity)
            : pinningForEntity;

        if (parsedPinning && typeof parsedPinning === "object") {
          setColumnPinning(parsedPinning);
        }
      } catch (error) {
        console.warn("Failed to parse persisted column pinning state:", error);
      }
    }
  }, [entity, globalTableConfig]);

  const handleColumnPinningChange = (updater: any) => {
    setColumnPinning((prev: any) => {
      const next = functionalUpdate(updater, prev);
      const normalizedPinning = normalizePinning(next, {
        enableRowSelection,
        showRowActions,
      });

      dispatch(
        setTableColumnPinning({
          entity,
          pinning: normalizedPinning,
        })
      );

      return normalizedPinning;
    });
  };

  return {
    columnPinning,
    setColumnPinning,
    columnPinningFromColumns,
    handleColumnPinningChange,
  };
}
